// A markdown text written in place (a step's body and result, a person's
// Context), until the document editor takes these over (a Case's Context
// already has: `LiveNoteField`).
//
// Promise:
// - Read, it is drawn with its references as chips (`RefText`); pressing the
//   text (not a chip) or Enter on it starts writing. Written, it is a plain
//   textarea without frame or ring (the caret says where you are).
// - Leaving the field (blur, Escape, ⌘/Ctrl+Enter) saves when the text
//   changed. A failed save keeps what was written in the field with one line
//   under it.
// - When someone else changed the text since it was read (`stale_note`), the
//   newer text is not overwritten: the field shows it, says so, and keeps what
//   was written beside it to put in on purpose or to drop.
import { m } from "@trameria/i18n";
import { useRef, useState } from "react";
import { ApiError } from "../data/api";
import { apiErrorMessage } from "../data/errors";
import { ErrorLine } from "../ui/bits";
import { Button } from "../ui/button";
import { isImeComposing } from "../ui/ime";
import { RefText } from "./ref-text";

const isStale = (error: unknown) =>
  error instanceof ApiError && error.code === "stale_note";

export function RefTextField({
  value,
  onSave,
  label,
  placeholder,
  maxLength,
  autoEdit = false,
  textClass = "text-[14px] leading-[1.7] text-body",
  minHeight = 24,
}: {
  /** The text as the server has it now. */
  value: string;
  /** Saves the text; rejects with the server's error. */
  onSave(text: string): Promise<unknown>;
  label: string;
  placeholder: string;
  maxLength?: number;
  /** Start writing at once, with the caret at the end (a result after closing a step). */
  autoEdit?: boolean;
  textClass?: string;
  minHeight?: number;
}) {
  const [draft, setDraft] = useState<string | null>(autoEdit ? value : null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const focusNext = useRef(autoEdit);

  const start = () => {
    focusNext.current = true;
    setError(null);
    setDraft(value);
  };

  const save = (text: string) => {
    setError(null);
    onSave(text).then(
      () => setConflict(null),
      (err) => {
        if (isStale(err)) {
          setConflict(text);
          return;
        }
        // Keep what was written, in the field, without taking the focus back.
        focusNext.current = false;
        setDraft(text);
        setError(apiErrorMessage(err, m.refs_save_failed_kept()));
      },
    );
  };

  const finish = () => {
    if (draft === null) return;
    const text = draft;
    setDraft(null);
    if (text !== value) save(text);
  };

  return (
    <div data-ref-text-field="">
      {draft !== null ? (
        <textarea
          aria-label={label}
          placeholder={placeholder}
          maxLength={maxLength}
          value={draft}
          ref={(el) => {
            if (!el || !focusNext.current) return;
            focusNext.current = false;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={finish}
          onKeyDown={(e) => {
            if (isImeComposing(e.nativeEvent)) return;
            if (
              e.key === "Escape" ||
              (e.key === "Enter" && (e.metaKey || e.ctrlKey))
            ) {
              if (e.key === "Enter") e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          className={`plain-field block w-full resize-none border-none bg-transparent p-0 outline-none [field-sizing:content] ${textClass}`}
          style={{ minHeight }}
        />
      ) : (
        // A text box that is read until pressed (Enter from the keyboard).
        // biome-ignore lint/a11y/useSemanticElements: it holds links (the chips), which a textarea cannot
        <div
          role="textbox"
          aria-readonly="true"
          aria-multiline="true"
          tabIndex={0}
          aria-label={label}
          data-ref-text-read=""
          onClick={(e) => {
            const hit = e.target as Element;
            if (hit.closest("a, button, [data-ref-missing]")) return;
            if (window.getSelection()?.toString()) return;
            start();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target === e.currentTarget) {
              e.preventDefault();
              start();
            }
          }}
          className={`cursor-text rounded-[4px] outline-offset-2 ${textClass}`}
          style={{ minHeight }}
        >
          {value ? (
            <RefText markdown={value} />
          ) : (
            <span className="text-placeholder">{placeholder}</span>
          )}
        </div>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
      {conflict !== null && (
        <div
          data-note-conflict=""
          className="mt-2 rounded-[8px] border border-border-soft bg-panel px-3 py-2.5"
        >
          <p role="alert" className="text-[12px] leading-[1.6] text-danger">
            {m.refs_note_conflict()}
          </p>
          <div className="mt-2 text-[11px] tracking-[0.04em] text-faint">
            {m.refs_note_conflict_yours()}
          </div>
          <RefText
            markdown={conflict}
            className="mt-1 text-[13px] leading-[1.6] text-body"
          />
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Button
              onClick={() => {
                const text = conflict;
                setConflict(null);
                save(text);
              }}
            >
              {m.refs_note_conflict_use_mine()}
            </Button>
            <Button variant="ghost" onClick={() => setConflict(null)}>
              {m.refs_note_conflict_drop()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
